import { useState, useRef, useCallback, useContext } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import { SurveyQuestionType } from '@trinity/chat/enums';
import type { SurveyFiltering } from '@trinity/chat/types/survey';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { cx } from '@trinity/chat-components/utils';
import * as arr from '@trinity/chat-components/utils/array.js';
import { AnalysisFilterButton } from '@trinity/chat-components/components/FilterButton/index.js';
import { useProjectSurveyResponsesFilterContext, useSurveyFilterDataContext } from '@/containers/GroupProject/Context';
import { getNumberOfActiveFilters } from '@/containers/GroupProject/utils/survey-filtering';
import { useValidFilterSurveys } from './hooks';
import { useFilterQuestion } from './hooks/useFilterQuestion';
import { MultiChoiceFilter } from './MultichoiceFilter';
import { SliderFilter } from './SliderFilter';
import styles from './style/SurveyResponseFilter.module.css';
import { TextFilter } from './TextFilter';
import type { IndexedFilters, OrderedFilter, FilterActions } from './utils';

interface ExtendedAppThemingContext {
  enabled: boolean;
  palette: object;
  theme?: {
    survey: {
      palette: {
        primary: {
          main: string;
        };
      };
    };
  };
}

export const SurveyResponseFilterContainer = () => {
  const filterContext = useProjectSurveyResponsesFilterContext();
  const filterDataContext = useSurveyFilterDataContext();
  const validSurveys = useValidFilterSurveys();

  if (!filterContext || filterDataContext.isLoading || validSurveys.length <= 0) {
    return null;
  }

  return (<SurveyResponseFilter />);
};

export const SurveyResponseFilter = () => {
  const filterContext = useProjectSurveyResponsesFilterContext();
  const filterDataContext = useSurveyFilterDataContext();
  const appThemingContext = useContext(AppThemingContext);
  const validSurveys = useValidFilterSurveys();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(undefined);
  const isLoading = filterDataContext.isLoading;
  const numFilters = getNumberOfActiveFilters(filterContext.filter);

  const filterState = (filterContext.filter ?
    arr.indexBy(filterContext.filter.filters, v => v.order)
    : {});
  const keyIncrement = useRef(0);

  if (!filterContext) {
    return null;
  }

  function setFilter(filter: IndexedFilters) {
    filterContext.setFilter({ filters: Object.values(filter) });
  }

  function clickAway() {
    if (isOpen) {
      setIsOpen(false);
    }
  }

  function toggleOpen() {
    if (!isLoading) {
      setIsOpen(!isOpen);
    }
  }

  function updateFilter(key: number, filter: OrderedFilter) {
    let keyToUse = key;
    const newFilter = { ...filterState };
    if (filter.questionId != newFilter[key].questionId) {
      delete newFilter[keyToUse];
      keyToUse = keyIncrement.current + 1;
      keyIncrement.current = keyIncrement.current + 1;
    }
    newFilter[keyToUse] = filter;
    setFilter(newFilter);
  }

  function addNewFilter() {
    const newFilter = { ...filterState };
    const newOrder = Object.keys(newFilter).length
      ? Math.max(...Object.values(newFilter).map(f => f.order)) + 1
      : 0;

    const survey = validSurveys[0];
    newFilter[keyIncrement.current + 1] = {
      surveyId: survey.surveyId,
      questionId: survey.questions[0].base.id,
      filter: null,
      order: newOrder,
    };
    keyIncrement.current = keyIncrement.current + 1;
    setFilter(newFilter);
  }

  function deleteFilter(key: number) {
    const newFilter = { ...filterState };
    delete newFilter[key];
    setFilter(newFilter);
  }

  const filterActions: FilterRowProps['actions'] = {
    deleteFilter,
    changeFilter: updateFilter,
  };

  return (
    <div>
      <ClickAwayListener onClickAway={clickAway}>
        <div>
          <AnalysisFilterButton
            ref={buttonRef}
            onClick={toggleOpen}
            count={numFilters}
            variant="survey-response"
            disabled={isLoading}
            aria-label="Filter survey responses"
            className={cx(isOpen ? styles.open : null)} />
          <Popper
            open={isOpen}
            anchorEl={buttonRef.current}
            className={styles.filterPopover}
            placement={'bottom-end'}>
            {filterState && Object.entries(filterState).sort(([, a], [, b]) => a.order - b.order).map(([k, f]) => (
              <FilterRow
                key={+k}
                accessor={+k}
                filter={f}
                actions={filterActions} />
            ))}
            <div
              className={cx(styles.filterRow, styles.addFilter)}
              onClick={addNewFilter}
              style={{
                color: appThemingContext?.enabled && (appThemingContext as unknown as ExtendedAppThemingContext)?.theme?.survey?.palette?.primary?.main
                  ? (appThemingContext as unknown as ExtendedAppThemingContext).theme.survey.palette.primary.main
                  : undefined,
                '--hover-color': appThemingContext?.enabled && (appThemingContext as unknown as ExtendedAppThemingContext)?.theme?.survey?.palette?.primary?.main
                  ? (appThemingContext as unknown as ExtendedAppThemingContext).theme.survey.palette.primary.main
                  : undefined,
                '--hover-bg': appThemingContext?.enabled && (appThemingContext as unknown as ExtendedAppThemingContext)?.theme?.survey?.palette?.primary?.main
                  ? `${(appThemingContext as unknown as ExtendedAppThemingContext).theme.survey.palette.primary.main}20`
                  : undefined,
              } as React.CSSProperties}>
              + Add Filter
            </div>
          </Popper>
        </div>
      </ClickAwayListener>
    </div>
  );
};

type FilterRowProps = {
  accessor: number;
  filter: SurveyFiltering.FilterEntry;
  actions: {
    deleteFilter: (key: number) => void;
    changeFilter: (key: number, filter: OrderedFilter) => void;
  };
};

const FilterRow = ({ filter: filterEntry, actions, accessor }: FilterRowProps) => {
  const question = useFilterQuestion(filterEntry);

  const filterActions: FilterActions = {
    deleteFilter: () => actions.deleteFilter(accessor),
    changeFilter: f => actions.changeFilter(accessor, f),
  };

  switch (question.typeId) {
    case SurveyQuestionType.Dropdown:
    case SurveyQuestionType.MultipleChoice:
    case SurveyQuestionType.Multiselect: {
      return (
        <MultiChoiceFilter
          filterActions={filterActions}
          filterEntry={filterEntry as SurveyFiltering.FilterEntry<SurveyQuestionType.MultipleChoice>} />
      );
    }
    case SurveyQuestionType.LongTextResponse:
    case SurveyQuestionType.ShortTextResponse: {
      return (
        <TextFilter
          filterActions={filterActions}
          filterEntry={filterEntry as SurveyFiltering.FilterEntry<SurveyQuestionType.LongTextResponse>} />
      );
    }
    case SurveyQuestionType.Sliders: {
      return (
        <SliderFilter
          filterActions={filterActions}
          filterEntry={filterEntry as SurveyFiltering.FilterEntry<SurveyQuestionType.Sliders>} />
      );
    }
    default: {
      console.log(`Missing question filter component for question type ${question.typeId}`);
      break;
    }
  }

  return null;
};